import {
  DialogType, DialogPositioningBehavior, DialogProps, HorizontalOriginOption, VerticalOriginOption, DialogWidget
} from '@tableau/widgets';
import * as React from 'react';

import { Formatters, Messages } from '../../../gen-src/Localize';
import { SiteUrlsProvider, SiteUrlsProviderProps } from '../DataCatalogProvider';
import { localizeDataQualityTypeName, stringToDataQualityType } from '../dataQualityWarnings/DataQualityType';
import { IndicatorDetailPanelDialogBodyStyle, IndicatorPanelDialogStyle } from '../style/DataQualityIndicatorStyles';
import { noopSiteUrls, SiteUrlsContext } from '../urls/SiteUrlsContext';
import { assertUnreachable } from '../utils/TypescriptHelpers';
import { DataQualityCertificationInteractiveButtonOrIcon, DataQualityWarningInteractiveButton, DataQualityWarningInteractiveIcon } from './DataQualityIndicatorButtons';
import { RenderAssetsIndicators } from './DataQualityIndicatorDetail';
import { mapRenderCertificationsToIndicatorProps, mapRenderWarningsToIndicatorsProps, CertificationStatusProps, WarningStatusProps } from './DataQualityIndicatorFragments';
import { DataQualityDetailPanelSide, DataQualityWarningKind, getTooltipAnchorClientRect, RenderIndicatorsProps } from './DataQualityIndicatorSharedHelpers';

function usePanelState<T>(testId: string) {
  const ref = React.useRef<T>(null);
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const onCloseRequest = React.useCallback(() => setIsDialogOpen(false), [setIsDialogOpen]);
  const onClick = React.useCallback(() => setIsDialogOpen(!isDialogOpen), [isDialogOpen, setIsDialogOpen]);

  return {
    isDialogOpen, onCloseRequest, onClick, testId, ref
  }
}

function shouldSkipWarningDueToNoIndicators(props: WarningStatusProps): boolean {
  switch (props.kind) {
    case 'graph': return props.anchoredWarnings.filter(t => t.isActive).length < 1 && props.upstreamWarnings.filter(t => t.isActive).length < 1;
    case 'vizportal': return props.anchoredWarnings.filter(t => t.isActive).length < 1 && props.upstreamWarnings.filter(t => t.isActive).length < 1;
    default:
      assertUnreachable(props);
      return true;
  }
}

function shouldSkipCertificationDueToNoIndicators(props: CertificationStatusProps): boolean {
  switch (props.kind) {
    case 'graph': return props.anchoredCertifications.filter(t => t.isActive).length < 1;
    case 'vizportal': return props.anchoredCertifications.filter(t => t.isActive).length < 1;
    default:
      assertUnreachable(props);
      return true;
  }
}

const DataQualityCertificationStatusChipId = 'data-quality-certification-chip-id';

export function EmbeddableCertificationStatusChipOrIcon(props: CertificationStatusProps & SiteUrlsProviderProps) {
  const { siteUrls, ...rest } = props;

  return (
    <SiteUrlsProvider siteUrls={siteUrls}>
      <CertificationStatusChipOrIcon {...rest} />
    </SiteUrlsProvider >
  );
}

export function CertificationStatusChipOrIcon(props: CertificationStatusProps): JSX.Element | null {
  const { isDialogOpen, onCloseRequest, ref, ...rest } = usePanelState<HTMLButtonElement>(DataQualityCertificationStatusChipId);

  if (shouldSkipCertificationDueToNoIndicators(props)) {
    return null;
  }

  const title = certificationTooltipTitle(props);
  const indicators = mapRenderCertificationsToIndicatorProps(props);

  return (
    <SiteUrlsContext.Provider value={props.urls || noopSiteUrls}>
      <DataQualityCertificationInteractiveButtonOrIcon {...rest} iconOnly={props.iconOnly} ref={ref} tabIndex={props.tabIndex} />
      {
        isDialogOpen &&
        <DataQualityIndicatorDetailPanel
          indicators={indicators}
          onCloseRequest={onCloseRequest}
          side={props.side || 'right'}
          title={title}
          anchorEl={ref.current as HTMLElement}
          stackingRootId={props.stackingRootId}
        />
      }
    </SiteUrlsContext.Provider>
  );
}

const DataQualityWarningStatusChipId = 'data-quality-warning-chip-id';

function buildWarningTooltipAndPanelProps(props: WarningStatusProps) {
  const warningKind = getWarningKind(props);
  const title = warningTooltipTitle(props);
  const indicators = mapRenderWarningsToIndicatorsProps(props);

  return { warningKind, title, indicators }
}

export function EmbeddableWarningStatusIcon(props: WarningStatusProps & SiteUrlsProviderProps) {
  const { siteUrls, ...rest } = props;

  return (
    <SiteUrlsProvider siteUrls={siteUrls}>
      <WarningStatusIcon {...rest} />
    </SiteUrlsProvider >
  );
}

export function WarningStatusIcon(props: WarningStatusProps): JSX.Element | null {
  const { isDialogOpen, onCloseRequest, ref, ...rest } = usePanelState<HTMLButtonElement>(DataQualityWarningStatusChipId);

  if (shouldSkipWarningDueToNoIndicators(props)) {
    return null;
  }

  const { warningKind, title, indicators } = buildWarningTooltipAndPanelProps(props);

  return (
    <SiteUrlsContext.Provider value={props.urls || noopSiteUrls}>
      <DataQualityWarningInteractiveIcon {...rest} warningKind={warningKind} title={title} ref={ref} tabIndex={props.tabIndex} />
      {
        isDialogOpen &&
        <DataQualityIndicatorDetailPanel
          indicators={indicators}
          onCloseRequest={onCloseRequest}
          side={props.side || 'right'}
          title={title}
          anchorEl={ref.current as HTMLElement}
          stackingRootId={props.stackingRootId}
        />
      }
    </SiteUrlsContext.Provider>
  )
}

export function EmbeddableWarningStatusChip(props: WarningStatusProps & SiteUrlsProviderProps) {
  const { siteUrls, ...rest } = props;

  return (
    <SiteUrlsProvider siteUrls={siteUrls}>
      <WarningStatusChip {...rest} />
    </SiteUrlsProvider >
  );
}

export function WarningStatusChip(props: WarningStatusProps): JSX.Element | null {
  const { isDialogOpen, onCloseRequest, ref, ...rest } = usePanelState<HTMLDivElement>(DataQualityWarningStatusChipId);

  if (shouldSkipWarningDueToNoIndicators(props)) {
    return null;
  }

  const { warningKind, title, indicators } = buildWarningTooltipAndPanelProps(props);

  return (
    <SiteUrlsContext.Provider value={props.urls || noopSiteUrls}>
      <DataQualityWarningInteractiveButton {...rest} warningKind={warningKind} title={title} ref={ref} tabIndex={props.tabIndex} />
      {
        isDialogOpen &&
        <DataQualityIndicatorDetailPanel
          indicators={indicators}
          onCloseRequest={onCloseRequest}
          side={props.side || 'right'}
          title={title}
          anchorEl={ref.current as HTMLElement}
          stackingRootId={props.stackingRootId}
        />
      }
    </SiteUrlsContext.Provider>
  )
}

function certificationTooltipTitle(props: CertificationStatusProps): string {
  if (props.anchoredCertifications.length <= 0) {
    return '';
  }

  switch (props.kind) {
    case 'vizportal':
      return localizeDataQualityTypeName(stringToDataQualityType(props.anchoredCertifications[0].type));
    case 'graph':
      return localizeDataQualityTypeName(stringToDataQualityType(props.anchoredCertifications[0].__typename));
    default:
      assertUnreachable(props);
      return '';
  }
}

function warningTooltipTitle(props: WarningStatusProps): string {
  const count = props.anchoredWarnings.length + props.upstreamWarnings.length;

  if (count <= 0) {
    return '';
  }

  if (count === 1) {
    if (props.anchoredWarnings.length === 1) {
      switch (props.kind) {
        case 'vizportal':
          return localizeDataQualityTypeName(stringToDataQualityType(props.anchoredWarnings[0].type));
        case 'graph':
          return localizeDataQualityTypeName(stringToDataQualityType(props.anchoredWarnings[0].warningType));
        default:
          assertUnreachable(props);
          return '';
      }
    }
  }

  return Messages.DataQualityIconMessage({
    MESSAGE_COUNT: count,
    MESSAGE_COUNT_LOC: Formatters.formatCount(count)
  });
}

function getWarningKind(props: WarningStatusProps): DataQualityWarningKind {
  switch (props.kind) {
    case 'graph': {
      if (props.anchoredWarnings.some(w => w.isSevere)) {
        return 'severe-warning';
      }
      break;
    }
    case 'vizportal': {
      if (props.anchoredWarnings.some(w => w.isSevere)) {
        return 'severe-warning';
      }
      break;
    }
    default:
      assertUnreachable(props);
  }

  return (props.upstreamWarnings.some(w => w.isSevere)) ? 'severe-warning' : 'standard-warning';
}

type DataQualityIndicatorDetailPanelProps = {
  readonly indicators: RenderIndicatorsProps;
  readonly title: string;
  readonly anchorEl?: HTMLElement;
  readonly onCloseRequest: () => void;
  readonly side: DataQualityDetailPanelSide;
  readonly stackingRootId?: string;
}

function DataQualityIndicatorDetailPanel(props: DataQualityIndicatorDetailPanelProps): JSX.Element {
  const dialogProps: DialogProps = {
    title: props.title || '',
    getAnchorClientRect: () => getTooltipAnchorClientRect(props.anchorEl),
    positioningBehavior: DialogPositioningBehavior.Anchored,
    dialogStyle: IndicatorPanelDialogStyle,
    dialogBodyStyle: IndicatorDetailPanelDialogBodyStyle,
    anchorOrigin: {
      vertical: VerticalOriginOption.top,
      horizontal:
        props.side === 'right'
          ? HorizontalOriginOption.right
          : HorizontalOriginOption.left,
    },
    showFooter: false,
    shouldAdjustPositionOnWindowResize: true,
    testId: 'data-quality-detail-panel',
    isModal: true,
    isGlassTransparent: true,
    type: DialogType.Uber,
    showCloseButton: false,
    handleGlassClick: props.onCloseRequest,
    onCloseRequest: props.onCloseRequest,
    shouldReturnFocusOnClose: false,
    disableAutoFocus: true,
    stackingRootId: props.stackingRootId,
  };

  return (
    <DialogWidget {...dialogProps}>
      <RenderAssetsIndicators {...props.indicators} />
    </DialogWidget>
  );
}
